import type { intelligence } from '@cwos/api-client';
import {
  chartPointState,
  formatMeasure,
  formatNumber,
  tableFromChart,
  tooSparseToPlot,
} from './format.js';
import { TableResult } from './TableResult.js';
import { niceScale } from '../../dashboard/analytics/chartMath.js';

/**
 * Trend, comparison, distribution, breakdown — drawn as self-contained SVG.
 *
 * No chart library and no network: the map surfaces in this repo already draw
 * their own SVG for the same reason (`packages/ui-admin/src/map/GeoMap.tsx`),
 * and a charting dependency inside an answer bubble would be a runtime that can
 * fail, a bundle that must be fetched, and a second visual language to keep in
 * step with the design tokens. Everything below is `<path>`, `<rect>` and
 * `<text>` in the house palette.
 *
 * IT DECLINES RATHER THAN DEGRADES. When fewer than two real values exist, the
 * component does not draw a thinner chart — it renders the SAME data as the
 * table it actually is. A trend through one point is a dot a reader will read
 * as a line; a bar chart of one bar has no comparison in it. See `format.ts`
 * `tooSparseToPlot`.
 *
 * A NULL IS A GAP, NEVER A ZERO. A missing point breaks the line rather than
 * dropping it to the floor, and draws no bar rather than a zero-length one.
 * Both are the same rule the KPI keeps: a measure nobody has is not a measure
 * of nothing.
 *
 * AND THERE ARE TWO KINDS OF GAP (F228). A bar can be empty because nothing
 * was recorded, or because a real figure was suppressed for small-N — two
 * different sentences that both arrive as `null`. The bar chart used to print
 * "no value" for both, which quietly told the reader an organisation did
 * nothing when in fact they were not allowed to see what it did.
 * `chartPointState` in `format.ts` makes the fork against the disclosure the
 * payload already carries, keyed by the point's own dimension key; the bar,
 * the LINE, the spoken summary, and the table under "Show the figures" all
 * read it, so none of the four can say a different thing about the same blank.
 *
 * THE LINE DID NOT READ IT, AND THIS PARAGRAPH SAID IT DID. `runsOf` and the
 * dot loop both forked on `point.value === null` and nothing else, so a
 * suppressed week and a week nobody recorded broke the line in exactly the
 * same place, in exactly the same way. The spoken summary counted the withheld
 * ones and the table beneath named them, which made the picture the only
 * surface of the four that disagreed — and the one a sighted reader looks at
 * first. A withheld gap is now MARKED in the plot and the mark is explained
 * beneath it.
 *
 * The picture is `role="img"` with a spoken summary, and the exact numbers are
 * always one disclosure away in a real table — a chart a screen reader cannot
 * read is a chart that answered nobody.
 *
 * Absent payload → renders nothing.
 */
export function ChartResult({ chart }: { chart: intelligence.ResultChart | null | undefined }) {
  if (chart === null || chart === undefined) return null;

  // Too sparse to plot honestly: render the same payload as its table. The
  // sentence says why, so a reader who expected a picture is not left wondering
  // whether something failed.
  if (tooSparseToPlot(chart)) {
    return (
      <div>
        <TableResult table={tableFromChart(chart)} />
        <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
          There is only one measured value here, so there is no shape to draw. The figures are shown
          as they are.
        </p>
      </div>
    );
  }

  return (
    <section className="rn-card card-record" aria-label={chart.title}>
      <div className="rn-card-head">
        <h3 className="rn-card-title">{chart.title}</h3>
        {chart.periodLabel !== undefined ? (
          <span className="rn-muted">{chart.periodLabel}</span>
        ) : null}
      </div>

      {chart.series.length > 1 ? <Legend series={chart.series} /> : null}

      {chart.shape === 'line' ? <LineChart chart={chart} /> : <BarChart chart={chart} />}

      {/* The bar chart writes the word "withheld" in place of each bar, so it
          explains itself. A line has no room for a word per point, so the mark
          is explained once here — and only when there is a mark to explain,
          because a standing sentence about suppression on a chart with none
          would invent a doubt. */}
      {chart.shape === 'line' && withheldCount(chart) > 0 ? (
        <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
          The dashed marks stand where a real figure is withheld rather than shown, because too few
          people stand behind it. They are withheld, not zero. A break in the line with no mark is a
          point nobody recorded.
        </p>
      ) : null}

      <details style={{ marginTop: 'var(--space-10)' }}>
        <summary className="rn-link" style={{ cursor: 'pointer' }}>
          Show the figures
        </summary>
        <div style={{ marginTop: 'var(--space-8)' }}>
          <TableResult table={tableFromChart(chart)} />
        </div>
      </details>
    </section>
  );
}

/**
 * Series colours, from the design tokens. Four, because the payload caps series
 * at four — a fifth series would be a colour this palette does not have, which
 * is why the schema will not accept one.
 */
const SERIES_COLORS = [
  'var(--color-sky)',
  'var(--color-navy)',
  'var(--color-good)',
  'var(--color-look)',
] as const;

function colorFor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length] ?? SERIES_COLORS[0];
}

function Legend({ series }: { series: readonly intelligence.ResultChartSeries[] }) {
  return (
    <ul
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-12)',
        listStyle: 'none',
        margin: '0 0 var(--space-12)',
        padding: 0,
      }}
    >
      {series.map((one, index) => (
        <li
          key={one.key}
          className="rn-muted"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-6)' }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: colorFor(index),
              display: 'inline-block',
            }}
          />
          {one.label}
        </li>
      ))}
    </ul>
  );
}

/** Every real value across every series — the domain the axes must cover. */
function realValues(chart: intelligence.ResultChart): number[] {
  return chart.series.flatMap((series) =>
    series.points.map((point) => point.value).filter((value): value is number => value !== null),
  );
}

/**
 * The vertical domain.
 *
 * The floor is zero whenever the data is non-negative, because a bar chart
 * whose baseline is not zero exaggerates every difference in it — the most
 * common way a truthful number becomes a misleading picture. Negative data gets
 * a floor at its own minimum, since a zero baseline there would push the plot
 * off the top.
 */
function domainOf(values: readonly number[]): { min: number; max: number } {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const floor = min < 0 ? min : 0;
  if (max === floor) return { min: floor, max: floor + 1 };
  return { min: floor, max };
}

/**
 * The line chart's axis. With a zero floor it is the analytics workspace's own
 * step-based nice scale (whole-number ticks for counts), so an Ask trend and a
 * dashboard trend of the same figure share one axis rule; min/mid/max of the
 * raw data drew ticks like 0 / 154.6 / 309.2. Negative data keeps its own floor.
 */
function lineAxis(values: readonly number[]): { min: number; max: number; ticks: number[] } {
  const { min, max } = domainOf(values);
  if (min < 0) return { min, max, ticks: [min, min + (max - min) / 2, max] };
  const scale = niceScale(Math.max(...values), values.every(Number.isInteger));
  return { min: 0, max: scale.max, ticks: scale.ticks };
}

/**
 * A spoken one-liner for the picture.
 *
 * F228: it counts the WITHHELD points and says so. A sighted reader can see
 * the word beside the empty bar; the only thing a screen-reader user gets from
 * an `<svg role="img">` is this string, so a suppression that is visible in
 * the picture and silent in its label is a suppression the screen reader never
 * learns about. The count is the point — the figures themselves are in the
 * table below, which renders the same fork cell by cell.
 */
/**
 * HOW MANY POINTS ARE WITHHELD — counted once, read by the spoken summary and
 * by the note under the line.
 *
 * Two call sites deciding this separately is how the picture and its caption
 * come to disagree, which is the family of defect this whole module is about.
 */
export function withheldCount(chart: intelligence.ResultChart): number {
  return chart.series
    .flatMap((series) => series.points)
    .filter((point) => chartPointState(point, chart.disclosure).kind === 'withheld').length;
}

function summaryOf(chart: intelligence.ResultChart): string {
  const all = chart.series.flatMap((series) => series.points);
  const withheld = withheldCount(chart);
  const suppression =
    withheld === 0
      ? ''
      : ` ${withheld} ${withheld === 1 ? 'figure is' : 'figures are'} withheld rather than measured at zero.`;
  return `${chart.shape === 'line' ? 'Trend' : 'Comparison'} of ${chart.title} across ${all.length} ${
    all.length === 1 ? 'point' : 'points'
  }.${suppression} The figures are listed below the chart.`;
}

// ---------------------------------------------------------------------------
// Line
// ---------------------------------------------------------------------------

const LINE_W = 640;
const LINE_H = 220;
const PAD_L = 56;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 34;

function LineChart({ chart }: { chart: intelligence.ResultChart }) {
  const values = realValues(chart);
  const { min, max, ticks } = lineAxis(values);
  const count = Math.max(...chart.series.map((series) => series.points.length));
  const plotW = LINE_W - PAD_L - PAD_R;
  const plotH = LINE_H - PAD_T - PAD_B;

  const x = (index: number) => PAD_L + (count <= 1 ? plotW / 2 : (index / (count - 1)) * plotW);
  const y = (value: number) => PAD_T + plotH - ((value - min) / (max - min)) * plotH;

  const first = chart.series[0];
  const labels = first === undefined ? [] : first.points.map((point) => point.label);

  return (
    <svg
      viewBox={`0 0 ${LINE_W} ${LINE_H}`}
      width="100%"
      height="auto"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={summaryOf(chart)}
      style={{ display: 'block', maxWidth: '100%', overflow: 'visible' }}
    >
      <GridAndAxis chart={chart} ticks={ticks} y={y} />
      {chart.series.map((series, seriesIndex) => (
        <g key={series.key}>
          {/* One <path> per UNBROKEN run of real values. A null does not join
              its neighbours across the gap — drawing through it would invent a
              measurement between two real ones. */}
          {runsOf(series.points).map((run) => (
            <path
              key={`${series.key}-${run[0]?.index ?? 0}`}
              d={run
                .map(
                  (entry, position) =>
                    `${position === 0 ? 'M' : 'L'} ${x(entry.index).toFixed(2)} ${y(entry.value).toFixed(2)}`,
                )
                .join(' ')}
              fill="none"
              stroke={colorFor(seriesIndex)}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
          {/* A WITHHELD GAP IS MARKED; AN ABSENT ONE IS NOT (F228).
              Both break the line, because neither is a measurement — but only
              one of them is a figure that EXISTS and is being kept from this
              reader, and a picture that draws the two identically tells them an
              organisation recorded nothing when in fact they may not see what
              it recorded. The rule marks the WITHHELD case rather than the
              absent one deliberately: the mark asserts something, and absence
              is the arm with nothing to assert.

              It publishes no figure and no headcount — only WHERE, which the
              disclosure this component was handed already names. */}
          {series.points.map((point, index) =>
            chartPointState(point, chart.disclosure).kind === 'withheld' ? (
              <line
                key={`${series.key}-withheld-${index}`}
                x1={x(index)}
                x2={x(index)}
                y1={PAD_T}
                y2={PAD_T + plotH}
                stroke="var(--color-ink2)"
                strokeWidth={1.5}
                strokeDasharray="3 3"
                opacity={0.55}
              />
            ) : null,
          )}
          {series.points.map((point, index) =>
            point.value === null ? null : (
              <circle
                key={`${series.key}-dot-${point.label}`}
                cx={x(index)}
                cy={y(point.value)}
                r={3}
                fill="var(--color-surface)"
                stroke={colorFor(seriesIndex)}
                strokeWidth={2}
              />
            ),
          )}
        </g>
      ))}
      {labels.map((label, index) =>
        // Only the ends and the middle are labelled: a dense axis of overlapping
        // text is less readable than three anchors plus the table below.
        index === 0 || index === labels.length - 1 || index === Math.floor(labels.length / 2) ? (
          <text
            key={`x-${label}`}
            x={x(index)}
            y={LINE_H - 12}
            textAnchor={index === 0 ? 'start' : index === labels.length - 1 ? 'end' : 'middle'}
            fontSize={11}
            fill="var(--color-ink2)"
          >
            {label}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/** Contiguous runs of real values, with their original index preserved. */
function runsOf(
  points: readonly intelligence.ResultChartPoint[],
): { index: number; value: number }[][] {
  const runs: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];
  points.forEach((point, index) => {
    if (point.value === null) {
      if (current.length > 0) runs.push(current);
      current = [];
      return;
    }
    current.push({ index, value: point.value });
  });
  if (current.length > 0) runs.push(current);
  // A run of one has no segment to draw; its dot is drawn separately.
  return runs.filter((run) => run.length > 1);
}

function GridAndAxis({
  chart,
  ticks,
  y,
}: {
  chart: intelligence.ResultChart;
  ticks: readonly number[];
  y: (value: number) => number;
}) {
  return (
    <g aria-hidden="true">
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD_L}
            x2={LINE_W - PAD_R}
            y1={y(tick)}
            y2={y(tick)}
            stroke="var(--color-line)"
            strokeWidth={1}
          />
          <text
            x={PAD_L - 8}
            y={y(tick) + 4}
            textAnchor="end"
            fontSize={11}
            fill="var(--color-ink2)"
          >
            {formatNumber(tick, chart.precision)}
          </text>
        </g>
      ))}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Bar
// ---------------------------------------------------------------------------

const BAR_W = 640;
const BAR_LABEL_W = 150;
const BAR_ROW_H = 26;
const BAR_GAP = 6;

/**
 * HORIZONTAL bars, because the labels are program names, site names and
 * worker-facing words — text that does not fit under a vertical bar without
 * being rotated or truncated, and a truncated label is a record nobody can
 * identify.
 */
function BarChart({ chart }: { chart: intelligence.ResultChart }) {
  const values = realValues(chart);
  const { min, max } = domainOf(values);
  const groups = chart.series[0]?.points.map((point) => point.label) ?? [];
  const perGroup = chart.series.length;
  const groupH = perGroup * BAR_ROW_H + BAR_GAP;
  const height = groups.length * groupH + 8;
  const plotW = BAR_W - BAR_LABEL_W - 60;
  const scale = (value: number) => ((value - min) / (max - min)) * plotW;

  return (
    <svg
      viewBox={`0 0 ${BAR_W} ${height}`}
      width="100%"
      height="auto"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={summaryOf(chart)}
      style={{ display: 'block', maxWidth: '100%' }}
    >
      {groups.map((label, groupIndex) => (
        <g key={label} transform={`translate(0 ${groupIndex * groupH})`}>
          <text
            x={BAR_LABEL_W - 10}
            y={groupH / 2 + 1}
            textAnchor="end"
            fontSize={12}
            fill="var(--color-ink)"
          >
            {label}
          </text>
          {chart.series.map((series, seriesIndex) => {
            const point = series.points.find((candidate) => candidate.label === label);
            const state = chartPointState(point, chart.disclosure);
            const y = seriesIndex * BAR_ROW_H + BAR_GAP / 2;
            if (state.kind !== 'value') {
              // NO BAR AT ALL, AND THE WORD INSTEAD — but F228: which word.
              //
              // A zero-length bar and a missing measurement look identical at
              // this scale, which is why a word is spent here at all. The bug
              // that word used to have is that it was the SAME word for two
              // different facts: a row the server measured and suppressed for
              // small-N read as "no value", telling the reader nothing was
              // recorded when in truth a real figure was kept from them. That
              // is the KPI tile's worst defect wearing a chart's typeface.
              //
              // `chartPointState` makes the fork against the disclosure the
              // payload already carries, keyed by the point's own dimension
              // key, and the two arms now say different things. The table
              // under "Show the figures" renders the SAME fork through
              // `tableFromChart`, so the picture and the figures beneath it
              // cannot disagree.
              //
              // THE WORD NO LONGER CARRIES A HEADCOUNT (F359). It used to read
              // "withheld (3 people)" when the server reported a cohort. That
              // parenthesis was the leak in its most compact form: an exact
              // count of a population this viewer cannot name, one bar per day
              // if they chose a daily grouping. The word "withheld" alone still
              // does the job the fork exists for — it is not "no value", so the
              // reader is told a real figure was kept from them rather than
              // that nothing was recorded.
              const withheld = state.kind === 'withheld';
              return (
                <text
                  key={series.key}
                  x={BAR_LABEL_W}
                  y={y + BAR_ROW_H / 2 + 4}
                  fontSize={11}
                  fill="var(--color-ink2)"
                  fontStyle="italic"
                >
                  {withheld ? 'withheld' : 'no value'}
                </text>
              );
            }
            const value = state.value;
            return (
              <g key={series.key}>
                <rect
                  x={BAR_LABEL_W}
                  y={y}
                  width={Math.max(1, scale(value))}
                  height={BAR_ROW_H - BAR_GAP}
                  rx={3}
                  fill={colorFor(seriesIndex)}
                />
                <text
                  x={BAR_LABEL_W + Math.max(1, scale(value)) + 8}
                  y={y + (BAR_ROW_H - BAR_GAP) / 2 + 4}
                  fontSize={11}
                  fill="var(--color-ink2)"
                >
                  {formatMeasure(value, chart.unit, chart.precision)}
                </text>
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
}
